/* cart-sample-guard.js
 * Blind Barrels – prevent free bonus kit / included items checkout without subscription.
 *
 * IDs for free / paid kits + subscriptions come from bbGuardConfig in localStorage,
 * which is written by the Subscription Plan Toggle section.
 *
 * Glass swap mapping (free → paid) is defined here explicitly, using the known variant IDs.
 */

(function () {
  const headersJSON = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  // Re-entrancy guard so ensureSampleGuard can't overlap runs
  let guardInFlight = false;

  // Map free glass variants → paid glass variants
  //
  // 44675174301903 -> 1 Custom Tasting Glass ($20 value, free)
  // 44683708498127 -> Blind Barrels Tasting Glass (paid $19.99)
  //
  // 44675174564047 -> 4 Custom Tasting Glasses ($80 value, free)
  // 44683694112975 -> 4 Custom Tasting Glasses (paid $79.99)
  const GLASS_MAPPING = {
    44675174301903: 44683708498127, // free single -> paid single
    44675174564047: 44683694112975, // free 4-pack -> paid 4-pack
  };

  function loadGuardConfig() {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
      const raw = window.localStorage.getItem('bbGuardConfig');
      if (!raw) return null;
      const cfg = JSON.parse(raw);
      if (
        !cfg ||
        !cfg.freeSampleVariantId ||
        !cfg.paidSampleVariantId ||
        !Array.isArray(cfg.subscriptionVariantIds) ||
        cfg.subscriptionVariantIds.length === 0
      ) {
        return null;
      }
      // freeGlassVariantIds is optional – default to [] if missing
      if (!Array.isArray(cfg.freeGlassVariantIds)) {
        cfg.freeGlassVariantIds = [];
      }
      return cfg;
    } catch (e) {
      console.error('[sampleGuard] Failed to parse bbGuardConfig:', e);
      return null;
    }
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!res || !res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('[sampleGuard] Failed to fetch cart:', e);
      return null;
    }
  }

  async function changeLine(line, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: headersJSON,
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) {
        console.warn('[sampleGuard] change.js failed for line', line, 'status', res.status);
      }
    } catch (e) {
      console.error('[sampleGuard] change.js error:', e);
    }
  }

  async function addVariant(variantId, quantity) {
    if (!variantId || !quantity) return;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: headersJSON,
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
        }),
      });
      if (!res.ok) {
        console.warn(
          '[sampleGuard] add.js failed for variant',
          variantId,
          'qty',
          quantity,
          'status',
          res.status
        );
      }
    } catch (e) {
      console.error('[sampleGuard] add.js error:', e);
    }
  }

  /**
   * Core guard:
   * - If cart has free sample box but no subscription:
   *     remove free sample and add paid single box instead.
   * - If cart has included glasses but no subscription:
   *     remove those included glasses AND add their paid equivalents.
   *
   * @returns {Promise<boolean>} true if any changes were applied
   */
  async function ensureSampleGuard() {
    // Prevent overlapping runs that would double-add items
    if (guardInFlight) {
      console.info('[sampleGuard] Skipping run; guard already in flight.');
      return false;
    }

    guardInFlight = true;
    try {
      const cfg = loadGuardConfig();
      if (!cfg) {
        // No config yet (user might have gone straight to cart without seeing the section).
        return false;
      }

      const FREE_SAMPLE_VARIANT_ID = cfg.freeSampleVariantId;
      const PAID_SINGLE_BOX_VARIANT_ID = cfg.paidSampleVariantId;
      const SUBSCRIPTION_VARIANT_IDS = cfg.subscriptionVariantIds;
      const FREE_GLASS_VARIANT_IDS = Array.isArray(cfg.freeGlassVariantIds)
        ? cfg.freeGlassVariantIds
        : [];

      const cart = await fetchCart();
      if (!cart || !Array.isArray(cart.items)) return false;

      let hasSubscription = false;

      const freeSampleLines = [];
      let totalFreeSampleQty = 0;

      const freeGlassLines = [];
      let anyFreeGlass = false;

      // Track how many paid glasses to add per paid variant ID
      const glassSwapTotals = {};

      cart.items.forEach((item, index) => {
        const variantId = item.variant_id;
        const quantity = item.quantity || 0;
        const lineNumber = index + 1;

        if (SUBSCRIPTION_VARIANT_IDS.includes(variantId)) {
          hasSubscription = true;
        }

        if (variantId === FREE_SAMPLE_VARIANT_ID && quantity > 0) {
          freeSampleLines.push({ lineNumber, quantity });
          totalFreeSampleQty += quantity;
        }

        if (FREE_GLASS_VARIANT_IDS.includes(variantId) && quantity > 0) {
          freeGlassLines.push({ lineNumber, quantity, variantId });
          anyFreeGlass = true;

          const paidId = GLASS_MAPPING[String(variantId)] || GLASS_MAPPING[variantId];
          if (paidId) {
            if (!glassSwapTotals[paidId]) glassSwapTotals[paidId] = 0;
            glassSwapTotals[paidId] += quantity;
          }
        }
      });

      // If there is any subscription in the cart, do nothing.
      if (hasSubscription) {
        return false;
      }

      // If there are no guarded items (no free sample, no included glasses), do nothing.
      if (!freeSampleLines.length && !anyFreeGlass) {
        return false;
      }

      console.info(
        '[sampleGuard] Found guarded items (free sample and/or included glasses) without subscription. Adjusting cart…'
      );

      //
      // IMPORTANT: remove all free lines in a *single* pass from
      // highest lineNumber → lowest, so Shopify's re-indexing
      // never invalidates the remaining line indices.
      //
      const allLinesToRemove = [...freeSampleLines, ...freeGlassLines].sort(
        (a, b) => b.lineNumber - a.lineNumber
      );

      for (const line of allLinesToRemove) {
        await changeLine(line.lineNumber, 0);
      }

      // 3) Replace free sample quantity with paid single box, if any
      if (totalFreeSampleQty > 0) {
        await addVariant(PAID_SINGLE_BOX_VARIANT_ID, totalFreeSampleQty);
      }

      // 4) For each paid glass variant we accumulated, add the corresponding quantity
      const paidIds = Object.keys(glassSwapTotals);
      for (const paidIdStr of paidIds) {
        const paidId = parseInt(paidIdStr, 10);
        const qty = glassSwapTotals[paidIdStr];
        if (paidId && qty > 0) {
          await addVariant(paidId, qty);
        }
      }

      return true;
    } finally {
      guardInFlight = false;
    }
  }

  function initSampleGuard() {
    if (
      typeof fetch !== 'function' ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    // Expose for cart.js (updateQuantity) to call after each cart change
    window.BlindBarrelsEnsureSampleGuard = ensureSampleGuard;

    // One initial cleanup in case user lands on cart with bad state
    ensureSampleGuard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSampleGuard);
  } else {
    initSampleGuard();
  }
})();
